import { useEffect, useRef, useState } from 'react';
import { Search, MapPin, Check } from 'lucide-react';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { airportApi, userApi } from '@/services/api';
import { useToast } from '@/components/ui/toast';
import { useAuthStore } from '@/stores/auth';

interface AirportOption {
  code: string;
  iata: string;
  icao: string;
  name: string;
  city: string;
  country: string;
}

interface BaseAirportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function BaseAirportModal({ isOpen, onClose }: BaseAirportModalProps) {
  const { user, updateUser } = useAuthStore();
  const { addToast } = useToast();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AirportOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AirportOption | null>(null);
  const [saving, setSaving] = useState(false);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setSelected(null);
      return;
    }

    const currentBase = user?.baseAirport;
    if (currentBase) {
      setQuery(currentBase);
    }
  }, [isOpen, user?.baseAirport]);

  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await airportApi.search(query);
        const data = res.data?.data || [];
        setResults(data);
      } catch {
        addToast({
          title: '搜索失败',
          description: '请稍后重试',
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [query, addToast]);

  const handleSave = async () => {
    const code = selected?.code || query.trim();
    if (!code) {
      addToast({
        title: '请选择机场',
        description: '请输入或选择基地机场代码',
        variant: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      await userApi.updateProfile({ baseAirport: code.toUpperCase() });
      updateUser({ baseAirport: code.toUpperCase() });
      addToast({
        title: '保存成功',
        description: `基地机场已设置为 ${code.toUpperCase()}`,
        variant: 'success',
      });
      onClose();
    } catch {
      addToast({
        title: '保存失败',
        description: '请稍后重试',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="设置基地机场">
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="输入机场代码或城市名称（如 PVG / 上海）"
            className="pl-10"
          />
        </div>

        {loading && (
          <p className="text-sm text-gray-500 text-center py-2">搜索中...</p>
        )}

        {!loading && results.length > 0 && (
          <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
            {results.map((airport) => {
              const isSelected = selected?.code === airport.code;
              return (
                <button
                  key={airport.code}
                  type="button"
                  onClick={() => setSelected(airport)}
                  className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {airport.code}
                        {airport.city && (
                          <span className="text-gray-500 font-normal"> · {airport.city}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {[airport.name, airport.country].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                </button>
              );
            })}
          </div>
        )}

        {!loading && query.length >= 2 && results.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-2">未找到匹配的机场</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
